import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Uppy from '@uppy/core';
import type { UppyFile, Meta, Body } from '@uppy/core';
import Tus from '@uppy/tus';
import { toast } from 'react-toastify';

/** Progress information for a single file */
export interface TusFileProgress {
    fileId: string;
    fileName: string;
    bytesUploaded: number;
    bytesTotal: number;
    percentage: number;
}

/** Information about a group of parts that form a single original file */
export interface TusPartGroup {
    groupId: string;
    originalFilename: string;
    tusIds: string[];
}

/** Result returned when all uploads complete */
export interface TusUploadResult {
    tusIds: string[];
    files: Array<{
        id: string;
        name: string;
        size: number;
        tusId: string;
    }>;
    /** Part groups for split files — server uses this to reassemble */
    partGroups: TusPartGroup[];
}

/** Configuration options for the hook */
export interface UseTusUploadOptions {
    /** TUS server endpoint (default: '/api/tus') */
    endpoint?: string;
    /** Chunk size in bytes (default: 50MB) */
    chunkSize?: number;
    /**
     * Files larger than this threshold (in bytes) will be split into multiple
     * parts and uploaded in parallel over separate connections, dramatically
     * improving throughput for large files. Default: 100MB.
     * Set to 0 to disable file splitting.
     */
    fileSplitThreshold?: number;
    /** Retry delays in ms (default: [0, 1000, 3000, 5000]) */
    retryDelays?: number[];
    /** Allowed MIME types */
    allowedFileTypes?: string[];
    /** Max file size in bytes */
    maxFileSize?: number;
    /** Max total size of all files in bytes */
    maxTotalSize?: number;
    /** Max number of files */
    maxNumberOfFiles?: number;
    /** Callback when progress updates */
    onProgress?: (progress: TusFileProgress) => void;
    /** Callback when all uploads complete */
    onComplete?: (result: TusUploadResult) => void;
    /** Callback on upload error */
    onError?: (error: Error, fileId?: string) => void;
    /** Callback when a file is added */
    onFileAdded?: (file: UppyFile<Meta, Body>) => void;
    /** Callback when a file is removed */
    onFileRemoved?: (file: UppyFile<Meta, Body>) => void;
}

/** A display-friendly file entry (may represent a group of split parts) */
export interface TusDisplayFile {
    /** Unique ID (either Uppy file ID or partGroupId) */
    id: string;
    /** Display name (original filename for split files) */
    name: string;
    /** Total file size in bytes */
    size: number;
    /** Whether this represents a group of split parts */
    isSplitGroup: boolean;
}

/** Return type of the useTusUpload hook */
export interface UseTusUploadReturn {
    /** List of files in Uppy (raw, includes individual parts) */
    files: UppyFile<Meta, Body>[];
    /** Display-friendly file list: split parts merged into original files */
    displayFiles: TusDisplayFile[];
    /** Progress map by display file ID (aggregated for split files) */
    displayProgress: Map<string, TusFileProgress>;
    /** Progress map by file ID (raw, per-part) */
    progress: Map<string, TusFileProgress>;
    /** Whether upload is in progress */
    isUploading: boolean;
    /** Whether upload is paused */
    isPaused: boolean;
    /** TUS upload IDs after completion */
    tusIds: string[];
    /** Whether there are files ready to upload */
    hasFiles: boolean;
    /** Total progress percentage across all files */
    totalProgress: number;
    /** Add files to the upload queue */
    addFiles: (files: File[]) => void;
    /** Remove a file from the upload queue */
    removeFile: (fileId: string) => void;
    /** Start uploading all files */
    startUpload: () => Promise<void>;
    /** Pause all uploads */
    pauseUpload: () => void;
    /** Resume all uploads */
    resumeUpload: () => void;
    /** Clear all files and reset state */
    clearFiles: () => void;
    /** The Uppy instance for advanced usage */
    uppy: Uppy<Meta, Body>;
}

// Infinity = send entire file/part as a single PATCH (tus-js-client default).
// This avoids HTTP round-trip gaps between chunks and TCP slow-start restarts.
// nginx client_max_body_size is set to 0 (unlimited) for /api/tus; @tus/server has its own maxSize.
const DEFAULT_CHUNK_SIZE = Infinity;
const DEFAULT_FILE_SPLIT_THRESHOLD = 100 * 1024 * 1024; // 100MB - files larger than this are split for parallel upload
const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB
const DEFAULT_RETRY_DELAYS = [0, 1000, 3000, 5000];

/**
 * Custom hook for resumable file uploads using TUS protocol
 *
 * @example
 * ```tsx
 * const {
 *   files,
 *   progress,
 *   isUploading,
 *   tusIds,
 *   addFiles,
 *   startUpload,
 *   pauseUpload,
 *   resumeUpload,
 *   clearFiles,
 * } = useTusUpload({
 *   endpoint: '/api/tus',
 *   maxFileSize: 500 * 1024 * 1024,
 *   onComplete: (result) => {
 *     console.log('Upload complete:', result.tusIds);
 *   },
 * });
 * ```
 */
export function useTusUpload(options: UseTusUploadOptions = {}): UseTusUploadReturn {
    const {
        endpoint = '/api/tus',
        chunkSize = DEFAULT_CHUNK_SIZE,
        fileSplitThreshold = DEFAULT_FILE_SPLIT_THRESHOLD,
        retryDelays = DEFAULT_RETRY_DELAYS,
        allowedFileTypes: _allowedFileTypes,
        maxFileSize = DEFAULT_MAX_FILE_SIZE,
        maxTotalSize,
        maxNumberOfFiles,
        onProgress,
        onComplete,
        onError,
        onFileAdded,
        onFileRemoved,
    } = options;

    // Store callbacks in refs to avoid re-creating Uppy on callback changes
    const callbacksRef = useRef({
        onProgress,
        onComplete,
        onError,
        onFileAdded,
        onFileRemoved,
    });

    // Update refs in effect to avoid modifying during render
    useEffect(() => {
        callbacksRef.current = {
            onProgress,
            onComplete,
            onError,
            onFileAdded,
            onFileRemoved,
        };
    });

    const [files, setFiles] = useState<UppyFile<Meta, Body>[]>([]);
    const [progress, setProgress] = useState<Map<string, TusFileProgress>>(new Map());
    const [isUploading, setIsUploading] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [tusIds, setTusIds] = useState<string[]>([]);
    const [totalProgress, setTotalProgress] = useState(0);

    // Track split-file part groups for reassembly during finalize
    const partGroupsRef = useRef<
        Map<string, { originalName: string; originalSize: number; originalType: string; partFileIds: string[] }>
    >(new Map());

    // Create Uppy instance once
    // Note: File type validation is handled at the UI layer (TusUploader's Dragger onChange)
    // We only set size/count restrictions here to avoid Uppy rejecting files due to MIME type mismatches
    const uppy = useMemo(() => {
        const instance = new Uppy<Meta, Body>({
            restrictions: {
                allowedFileTypes: null,
                maxFileSize,
                maxTotalFileSize: maxTotalSize || null,
                maxNumberOfFiles: maxNumberOfFiles || null,
            },
            autoProceed: false,
        });

        instance.use(Tus, {
            endpoint,
            chunkSize,
            retryDelays,
            limit: 6, // 6 concurrent uploads — allows all parts of a split file to upload in parallel
            removeFingerprintOnSuccess: true, // Remove fingerprint after success to prevent stale IDs on re-upload
            headers: {
                // Will be populated by Uppy with TUS-specific headers
            },
        });

        return instance;
    }, [endpoint, chunkSize, retryDelays, maxFileSize, maxTotalSize, maxNumberOfFiles]);

    // Set up event handlers
    useEffect(() => {
        const handleFileAdded = (file: UppyFile<Meta, Body>) => {
            setFiles(uppy.getFiles());
            callbacksRef.current.onFileAdded?.(file);
        };

        const handleFileRemoved = (file: UppyFile<Meta, Body>) => {
            setFiles(uppy.getFiles());
            setProgress((prev: Map<string, TusFileProgress>) => {
                const next = new Map(prev);
                next.delete(file.id);
                return next;
            });
            callbacksRef.current.onFileRemoved?.(file);
        };

        const handleUploadProgress = (
            file: UppyFile<Meta, Body> | undefined,
            progressData: { bytesUploaded: number; bytesTotal: number | null }
        ) => {
            if (!file) return;
            const bytesTotal = progressData.bytesTotal ?? 0;

            const fileProgress: TusFileProgress = {
                fileId: file.id,
                fileName: file.name || 'unknown',
                bytesUploaded: progressData.bytesUploaded,
                bytesTotal,
                percentage: bytesTotal > 0 ? Math.round((progressData.bytesUploaded / bytesTotal) * 100) : 0,
            };

            setProgress((prev: Map<string, TusFileProgress>) => new Map(prev).set(file.id, fileProgress));
            callbacksRef.current.onProgress?.(fileProgress);
        };

        const handleProgress = (progressValue: number) => {
            setTotalProgress(progressValue);
        };

        const handleComplete = (result: { successful?: UppyFile<Meta, Body>[]; failed?: UppyFile<Meta, Body>[] }) => {
            setIsUploading(false);

            const successfulFiles = result.successful || [];

            // Extract TUS IDs from successful uploads
            const uploadedTusIds = successfulFiles
                .map(file => {
                    // TUS stores the upload URL in file.response?.uploadURL
                    const uploadUrl = file.response?.uploadURL;
                    if (uploadUrl) {
                        // Extract ID from URL (last segment)
                        const urlParts = uploadUrl.split('/');
                        return { fileId: file.id, tusId: urlParts[urlParts.length - 1] };
                    }
                    return null;
                })
                .filter((item): item is { fileId: string; tusId: string } => item !== null);

            const allTusIds = uploadedTusIds.map(item => item.tusId);
            setTusIds(allTusIds);

            // Build a fileId→tusId lookup for part group resolution
            const fileIdToTusId = new Map(uploadedTusIds.map(item => [item.fileId, item.tusId]));

            // Build part groups: map partGroupId → ordered TUS IDs
            const partGroupsList: TusPartGroup[] = [];
            for (const [groupId, group] of partGroupsRef.current.entries()) {
                const groupTusIds = group.partFileIds
                    .map((fid: string) => fileIdToTusId.get(fid))
                    .filter((id: string | undefined): id is string => id !== undefined);
                if (groupTusIds.length > 0) {
                    partGroupsList.push({
                        groupId,
                        originalFilename: group.originalName,
                        tusIds: groupTusIds,
                    });
                }
            }

            const uploadResult: TusUploadResult = {
                tusIds: allTusIds,
                files: successfulFiles.map((file, index) => ({
                    id: file.id,
                    name: file.name || 'unknown',
                    size: file.size || 0,
                    tusId: allTusIds[index] || '',
                })),
                partGroups: partGroupsList,
            };

            callbacksRef.current.onComplete?.(uploadResult);
        };

        const handleError = (error: { name: string; message: string; details?: string }) => {
            callbacksRef.current.onError?.(new Error(error.message), undefined);
        };

        const handleUploadError = (
            file: UppyFile<Meta, Body> | undefined,
            error: { name: string; message: string; details?: string }
        ) => {
            callbacksRef.current.onError?.(new Error(error.message), file?.id);
        };

        const handlePauseAll = () => {
            setIsPaused(true);
        };

        const handleResumeAll = () => {
            setIsPaused(false);
        };

        const handleRestrictionFailed = (file: UppyFile<Meta, Body> | undefined, error: { message: string }) => {
            toast.warning(`${file?.name ?? 'File'}: ${error.message}`);
        };

        uppy.on('file-added', handleFileAdded);
        uppy.on('file-removed', handleFileRemoved);
        uppy.on('upload-progress', handleUploadProgress);
        uppy.on('progress', handleProgress);
        uppy.on('complete', handleComplete);
        uppy.on('error', handleError);
        uppy.on('upload-error', handleUploadError);
        uppy.on('pause-all', handlePauseAll);
        uppy.on('resume-all', handleResumeAll);
        uppy.on('restriction-failed', handleRestrictionFailed);

        return () => {
            uppy.off('file-added', handleFileAdded);
            uppy.off('file-removed', handleFileRemoved);
            uppy.off('upload-progress', handleUploadProgress);
            uppy.off('progress', handleProgress);
            uppy.off('complete', handleComplete);
            uppy.off('error', handleError);
            uppy.off('upload-error', handleUploadError);
            uppy.off('pause-all', handlePauseAll);
            uppy.off('resume-all', handleResumeAll);
            uppy.off('restriction-failed', handleRestrictionFailed);
        };
    }, [uppy]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            uppy.cancelAll();
        };
    }, [uppy]);

    const addFiles = useCallback(
        (filesToAdd: File[]) => {
            filesToAdd.forEach(file => {
                try {
                    // Split large files into parts for parallel upload over multiple connections
                    if (fileSplitThreshold > 0 && file.size > fileSplitThreshold) {
                        // Calculate number of parts: 1 part per threshold, capped at 6
                        const numParts = Math.min(Math.ceil(file.size / fileSplitThreshold), 6);
                        const partSize = Math.ceil(file.size / numParts);
                        const groupId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
                        const partFileIds: string[] = [];

                        for (let i = 0; i < numParts; i++) {
                            const start = i * partSize;
                            const end = Math.min(start + partSize, file.size);
                            const blob = file.slice(start, end);
                            const partName = `${file.name}__part_${i + 1}_${numParts}`;

                            const fileId = uppy.addFile({
                                name: partName,
                                type: file.type,
                                data: blob,
                                meta: {
                                    filename: partName,
                                    filetype: file.type,
                                    // Part metadata — server reads these from TUS Upload-Metadata
                                    // to reassemble parts into the original file during finalize
                                    partGroupId: groupId,
                                    partIndex: String(i),
                                    totalParts: String(numParts),
                                    originalFilename: file.name,
                                },
                            });
                            partFileIds.push(fileId);
                        }

                        partGroupsRef.current.set(groupId, {
                            originalName: file.name,
                            originalSize: file.size,
                            originalType: file.type,
                            partFileIds,
                        });
                    } else {
                        // Small files upload as a single TUS resource
                        uppy.addFile({
                            name: file.name,
                            type: file.type,
                            data: file,
                            meta: {
                                filename: file.name,
                                filetype: file.type,
                            },
                        });
                    }
                } catch (err) {
                    // File may already exist or fail validation
                    console.warn('Failed to add file:', file.name, err);
                }
            });
        },
        [uppy, fileSplitThreshold]
    );

    const removeFile = useCallback(
        (fileId: string) => {
            // Check if fileId is a partGroupId or a part within a group
            for (const [groupId, group] of partGroupsRef.current.entries()) {
                if (groupId === fileId || group.partFileIds.includes(fileId)) {
                    group.partFileIds.forEach((id: string) => {
                        try {
                            uppy.removeFile(id);
                        } catch {
                            /* already removed */
                        }
                    });
                    partGroupsRef.current.delete(groupId);
                    return;
                }
            }
            uppy.removeFile(fileId);
        },
        [uppy]
    );

    const startUpload = useCallback(async () => {
        setIsUploading(true);
        setIsPaused(false);
        try {
            await uppy.upload();
        } catch (err) {
            setIsUploading(false);
            throw err;
        }
    }, [uppy]);

    const pauseUpload = useCallback(() => {
        uppy.pauseAll();
    }, [uppy]);

    const resumeUpload = useCallback(() => {
        uppy.resumeAll();
    }, [uppy]);

    const clearFiles = useCallback(() => {
        uppy.cancelAll();
        setFiles([]);
        setProgress(new Map());
        setTusIds([]);
        setTotalProgress(0);
        setIsUploading(false);
        setIsPaused(false);
        partGroupsRef.current.clear();
    }, [uppy]);

    // Build display-friendly file list: merge split parts into original files
    // This runs on every files/progress change so the UI always shows originals
    // partGroupsRef is read here but is effectively synchronized with `files` —
    // parts are added to the ref in the same event handler that triggers file state changes.
    /* eslint-disable react-hooks/refs */
    const { displayFiles, displayProgress } = useMemo(() => {
        const partFileIdSet = new Set<string>();
        for (const group of partGroupsRef.current.values()) {
            for (const id of group.partFileIds) {
                partFileIdSet.add(id);
            }
        }

        const dFiles: TusDisplayFile[] = [];
        const dProgress = new Map<string, TusFileProgress>();

        // Add merged entries for each part group (original file)
        for (const [groupId, group] of partGroupsRef.current.entries()) {
            dFiles.push({
                id: groupId,
                name: group.originalName,
                size: group.originalSize,
                isSplitGroup: true,
            });

            // Aggregate progress across all parts in this group
            let totalUploaded = 0;
            let totalSize = 0;
            for (const partId of group.partFileIds) {
                const p = progress.get(partId);
                if (p) {
                    totalUploaded += p.bytesUploaded;
                    totalSize += p.bytesTotal;
                } else {
                    // Part hasn't started yet — use its Uppy file size
                    const uppyFile = files.find((f: UppyFile<Meta, Body>) => f.id === partId);
                    totalSize += uppyFile?.size || 0;
                }
            }
            dProgress.set(groupId, {
                fileId: groupId,
                fileName: group.originalName,
                bytesUploaded: totalUploaded,
                bytesTotal: totalSize || group.originalSize,
                percentage: totalSize > 0 ? Math.round((totalUploaded / totalSize) * 100) : 0,
            });
        }

        // Add non-part files as-is
        for (const file of files) {
            if (!partFileIdSet.has(file.id)) {
                dFiles.push({
                    id: file.id,
                    name: file.name || 'unknown',
                    size: file.size || 0,
                    isSplitGroup: false,
                });
                const p = progress.get(file.id);
                if (p) {
                    dProgress.set(file.id, p);
                }
            }
        }

        return { displayFiles: dFiles, displayProgress: dProgress };
    }, [files, progress]);
    /* eslint-enable react-hooks/refs */

    return {
        files,
        displayFiles,
        displayProgress,
        progress,
        isUploading,
        isPaused,
        tusIds,
        hasFiles: files.length > 0,
        totalProgress,
        addFiles,
        removeFile,
        startUpload,
        pauseUpload,
        resumeUpload,
        clearFiles,
        uppy,
    };
}

export default useTusUpload;
